import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { ConversationsResponse, SentResponse, ThreadResponse } from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`), for messaging between connected members (ADR 052, `MEM-054`, `MEM-055`).
 *
 * **Everything here needs a session, and there is no signed-out version of any of it.** A thread
 * is the most private thing this platform holds about two people — ADR 052 says so in as many
 * words.
 *
 * **No path carries a member id.** The reader is the session; the person on the other side is
 * named by `connectionRef`, which the API derives and this client only ever echoes back, through
 * `encodeURIComponent`.
 *
 * **There is no hook here that blocks, reports, deletes or edits.** ADR 052 and `OI-12`: MVP
 * builds none of them, and disconnecting — `useRemoveConnection`, next door — is the member's
 * control.
 */

export const CONVERSATIONS_KEY = ['messages', 'conversations'] as const;
const threadKey = (connectionRef: string): readonly unknown[] => [
  'messages',
  'thread',
  connectionRef,
];

/** `MEM-055` AC-2 — the Messages page. Most recently active first; the server does the ordering. */
export function useMyConversations(options: {
  readonly enabled: boolean;
}): UseQueryResult<ConversationsResponse> {
  return useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => apiRequest<ConversationsResponse>('/api/messages'),
    enabled: options.enabled,
    retry: false,
  });
}

/**
 * `MEM-054` AC-2 — one thread.
 *
 * **This does not mark anything read.** Opening is {@link useMarkThreadRead}, which the thread
 * calls once it is actually on screen: a read that cleared the count would make a prefetch, a
 * back button and a notification tap all silently swallow the dot.
 */
export function useThread(
  connectionRef: string | null,
  options: { readonly enabled: boolean },
): UseQueryResult<ThreadResponse> {
  return useQuery({
    queryKey: threadKey(connectionRef ?? ''),
    queryFn: () =>
      apiRequest<ThreadResponse>(`/api/messages/${encodeURIComponent(connectionRef ?? '')}`),
    enabled: options.enabled && (connectionRef ?? '').length > 0,
    retry: false,
  });
}

/**
 * `MEM-054` AC-3 — send.
 *
 * The thread is refetched rather than optimistically appended. An optimistic bubble on a thread
 * whose send was refused — the pair disconnected since the screen loaded (AC-5) — would show a
 * member their own message sitting in a conversation it never reached, which is the one lie this
 * screen must not tell. The refusal surfaces as an error the composer renders instead.
 *
 * The conversations list is invalidated with it: the row's preview, its time and its order all
 * moved.
 */
export function useSendMessage(
  connectionRef: string,
): UseMutationResult<SentResponse, Error, { readonly body: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body }) =>
      apiRequest<SentResponse>(`/api/messages/${encodeURIComponent(connectionRef)}`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: threadKey(connectionRef) });
      await queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}

/**
 * `MEM-055` AC-3 — opening a thread clears its unread state.
 *
 * Fire-and-forget from the caller's point of view: a failure here leaves a dot on a thread the
 * member has read, which is a small wrongness, and the alternative — an error toast over a
 * conversation they are trying to read — is a larger one. The list is invalidated on success so
 * the *New* pill and the navigation dot both go.
 */
export function useMarkThreadRead(): UseMutationResult<void, Error, { readonly connectionRef: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ connectionRef }) => {
      await apiRequest<void>(`/api/messages/${encodeURIComponent(connectionRef)}/read`, {
        method: 'POST',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}
